/**
 * RegisterPage — new account / new company onboarding screen.
 *
 * Visual rebuild only. The signup contract is UNCHANGED:
 *   • Calls `useAuth().register(email, password, name, companyName, licenseKey)`
 *     which posts to /api/auth/register. The backend decides whether the
 *     license key creates a new company OR joins an existing one — the
 *     frontend just collects fields and forwards them.
 *   • License key is required (validation kept as-is).
 *   • Company name is optional (helper text says "leave blank if joining
 *     existing company via team key" — backend enforces that branch).
 *
 * Do not rewire the form to a different endpoint or change the field
 * names without updating the AuthContext register() signature first.
 */
import { useState } from 'react';
import { Key } from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '@/contexts/AuthContext';
import AuthShell, { AuthHeader, AuthAlert, AuthFootLink } from '@/components/auth/AuthShell';
import AuthInput from '@/components/auth/AuthInput';
import AuthSubmit from '@/components/auth/AuthSubmit';
import { PasswordRules } from '@/components/auth/PasswordRules';
import { PASSWORD_MIN_LENGTH, passwordRuleError } from '@/lib/passwordPolicy';

export default function RegisterPage() {
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [licenseKey, setLicenseKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!licenseKey.trim()) {
      setError('License key is required');
      return;
    }
    const pwErr = passwordRuleError(password);
    if (pwErr) {
      setError(pwErr);
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name, companyName, licenseKey);
      // On success AuthContext sets the user and the app-level
      // PublicRoute redirects automatically — no navigate() needed here.
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell testId="register-page">
      <AuthHeader
        title="Create Account"
        subtitle="Enter your license key to get started."
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && <AuthAlert kind="error" testId="register-error">{error}</AuthAlert>}

        <AuthInput
          id="register-license"
          label="License Key"
          value={licenseKey}
          onChange={(e) => setLicenseKey(e.target.value)}
          required
          placeholder="LK-XXXX-XXXX"
          leftIcon={<Key className="w-4 h-4" />}
          inputClassName="font-mono tracking-wider"
          testId="register-license-input"
          onUpperCase
        />

        <AuthInput
          id="register-name"
          label="Full Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoComplete="name"
          placeholder="John Smith"
          testId="register-name-input"
        />

        <AuthInput
          id="register-company"
          label="Company Name"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          autoComplete="organization"
          placeholder="Your Landscape Company"
          helperText="Leave blank if joining an existing company via team key."
          testId="register-company-input"
        />

        <AuthInput
          id="register-email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          placeholder="you@company.com"
          testId="register-email-input"
        />

        <div>
          <AuthInput
            id="register-password"
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={PASSWORD_MIN_LENGTH}
            autoComplete="new-password"
            placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
            withVisibilityToggle
            testId="register-password-input"
          />
          <PasswordRules password={password} testId="register-password-rules" />
        </div>

        <AuthSubmit
          loading={loading}
          loadingLabel="Creating account…"
          testId="register-submit-button"
        >
          Activate &amp; Create Account
        </AuthSubmit>

        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }} />
          </div>
        </div>

        <AuthFootLink
          prompt="Already have an account?"
          label="Sign in"
          to="/login"
          testId="login-link"
        />
      </form>
    </AuthShell>
  );
}
